<script>
	import { onDestroy, onMount, tick } from 'svelte';
	import { getApiUrl, getDivisionTitle, injectGlobalStyles, loadJson, normalizeCollection, gameUrl } from './shared.js';
	import { isAuthorized } from './auth.js';
	import { fetchBettings, getBetPercentages, makeBet, resolveBettingDate } from './betting.js';
	import AuthDialog from './AuthDialog.svelte';
	import WidgetSelect from './WidgetSelect.svelte';

	export let projectId = '';
	export let tag = 'mlbl';

	const ALL_DATES_KEY = '';
	const ALL_DATES_LABEL = 'Все даты';

	let host;
	let railElement;
	let divisions = [];
	let selectedLeagueId = '';
	let selectedLeagueTag = '';
	let games = [];
	let selectedDateKey = ALL_DATES_KEY;
	let loadingDivisions = false;
	let loadingSchedule = false;
	let errorMessage = '';
	let syncingFromGlobal = false;

	// Состояние конкурса прогнозов
	let bettingsByProviderId = {};
	let bettingLoading = false;
	let authOpen = false;
	let pendingBet = null;
	let placingBetKey = '';
	let betError = '';

	const LEAGUE_CHANGED_EVENT = 'ilb:league-changed';

	// Переменные для свайпа
	let isDragging = false;
	let startX = 0;
	let scrollLeftStart = 0;

	$: leagueOptions = buildLeagueOptions(divisions);
	$: dateOptions = buildDateOptions(games);
	
	// Фильтрация игр по выбранному ключу даты
	$: filteredGames = selectedDateKey
		? games.filter((game) => getGameDateKey(game) === selectedDateKey)
		: games;

	$: gameKey = (game, index) =>
		game?.id ?? `${getGameDateKey(game)}-${game?.team1?.name ?? ''}-${game?.team2?.name ?? ''}-${index}`;

	function getLeagueSelectionStore() {
		const globalScope = window;
		if (!globalScope.__ilbSelectedLeagueByProject) {
			globalScope.__ilbSelectedLeagueByProject = {};
		}
		return globalScope.__ilbSelectedLeagueByProject;
	}

	function getStoredLeagueId() {
		if (!projectId) return '';
		return getLeagueSelectionStore()[projectId] || '';
	}

	function rememberLeagueId(leagueId) {
		if (!projectId || !leagueId) return;
		getLeagueSelectionStore()[projectId] = leagueId;
	}

	function publishLeagueSelection(leagueId) {
		if (!projectId || !leagueId) return;
		window.dispatchEvent(new CustomEvent(LEAGUE_CHANGED_EVENT, {
			detail: { projectId, leagueId }
		}));
	}

	function handleGlobalLeagueChange(event) {
		const payload = event?.detail;
		if (!payload || payload.projectId !== projectId) return;
		if (!payload.leagueId || payload.leagueId === selectedLeagueId) return;
		if (!leagueOptions.some((item) => item.value === payload.leagueId)) return;
		syncingFromGlobal = true;
		selectedLeagueId = payload.leagueId;
	}

	function buildLeagueOptions(items) {
		return (items || []).map((division, index) => ({
			value: division?.id || division?._id || division?.providerId || `division-${index}`,
			label: getDivisionTitle(division)
		}));
	}

	function getLeagueTag(league) {
		return league?.tag || league?.Tag || '';
	}

	function getSelectedLeague() {
		return divisions.find((division) => {
			const id = division?.id || division?._id || division?.providerId || '';
			return id && String(id) === String(selectedLeagueId);
		}) || null;
	}

	function normalizeDateString(dateStr) {
		if (!dateStr || typeof dateStr !== 'string') return null;
		const parts = dateStr.split('.');
		if (parts.length === 3) {
			const day = parts[0].padStart(2, '0');
			const month = parts[1].padStart(2, '0');
			const year = parts[2];
			return `${year}-${month}-${day}`;
		}
		if (dateStr.includes('-')) {
			return dateStr.substring(0, 10);
		}
		return null;
	}

	function getGameDateKey(game) {
		const localDateStr = game?.dateTime?.localDate;
		return localDateStr;
	}

	function formatDisplayDate(dateStr) {
		if (!dateStr) return 'Без даты';
		const parts = dateStr.split('.');
		if (parts.length === 3) {
			const day = parseInt(parts[0], 10);
			const monthIndex = parseInt(parts[1], 10) - 1;
			const months = [
				'Января', 'Февраля', 'Марта', 'Апреля', 'Мая', 'Июня',
				'Июля', 'Августа', 'Сентября', 'Октября', 'Ноября', 'Декабря'
			];
			return `${day} ${months[monthIndex] || ''}`;
		}
		return dateStr;
	}

	function getGameTimeLabel(game) {
		return game?.dateTime?.localTime || '';
	}

	function isOnlineGame(game) {
		if (game?.isOnline === true) return true;
		const status = game?.status;
		return status === 2 || status === 'Online' || status === 'online';
	}

	function isCompletedGame(game) {
		const status = game?.status;
		return status === 1 || status === 'Completed' || status === 'completed' || status === 'Result' || status === 'ResultConfirmed';
	}

	function hasScore(game) {
		return (game?.score1 !== null && game?.score1 !== undefined) || 
		       (game?.score2 !== null && game?.score2 !== undefined);
	}

	function getOnlineInfo(game) {
		const online = game?.online;
		if (!online) return 'Онлайн';
		const parts = [];
		if (online.period) parts.push(`${online.period}-й период`);
		if (online.timeToGo) parts.push(online.timeToGo);
		if (online.ot) parts.push(`OT ${online.ot}`);
		return parts.length ? parts.join(' • ') : 'Онлайн';
	}

	function buildDateOptions(items) {
		const map = new Map();
		for (const game of items || []) {
			const key = getGameDateKey(game);
			if (key !== '__no_date__' && !map.has(key)) {
				const label = game.dateTime?.localDate 
					? formatDisplayDate(game.dateTime.localDate) 
					: key;
				map.set(key, label);
			}
		}

		const options = Array.from(map.entries()).map(([key, label]) => ({ value: key, label }));
		
		const allOption = { value: ALL_DATES_KEY, label: ALL_DATES_LABEL };
		
		if (selectedDateKey && selectedDateKey !== ALL_DATES_KEY && !options.some((item) => item.value === selectedDateKey)) {
			selectedDateKey = ALL_DATES_KEY;
		}

		return [allOption, ...options];
	}

	async function loadDivisions() {
		if (!projectId) { divisions = []; return; }
		loadingDivisions = true;
		errorMessage = '';
		try {
			const url = `${getApiUrl()}/stats/project/${encodeURIComponent(projectId)}/leagues`;
			let rawDivisions = normalizeCollection(await loadJson(url));

			divisions = rawDivisions;

			const options = buildLeagueOptions(divisions);
			const storedLeagueId = getStoredLeagueId();

			if (storedLeagueId && options.some((item) => item.value === storedLeagueId)) {
				selectedLeagueId = storedLeagueId;
			} else if (!selectedLeagueId || !options.some((item) => item.value === selectedLeagueId)) {
				selectedLeagueId = options[0]?.value || '';
			}

		} catch (error) {
			console.error("Load Divisions Error:", error);
			errorMessage = `Ошибка загрузки дивизионов`;
		} finally { 
			loadingDivisions = false; 
		}
	}

	// Реактивный блок: следит за изменением selectedLeagueId
	$: {
		if (selectedLeagueId && !loadingDivisions) {
			rememberLeagueId(selectedLeagueId);
			if (!syncingFromGlobal) {
				publishLeagueSelection(selectedLeagueId);
			} else {
				syncingFromGlobal = false;
			}
			loadSchedule(selectedLeagueId);
		}
	}

	async function loadSchedule(divisionId) {     
		const targetDivisionId = divisionId;
		
		selectedDateKey = ALL_DATES_KEY;
		selectedLeagueTag = getLeagueTag(getSelectedLeague());
		
		if (!targetDivisionId) { 
			games = []; 
			return; 
		}
		
		loadingSchedule = true;
		errorMessage = '';
		games = []; 
		bettingsByProviderId = {};

		try {
			const url = `${getApiUrl()}/stats/competition/${encodeURIComponent(targetDivisionId)}/schedule`;
			const scheduleData = normalizeCollection(await loadJson(url));

			if (selectedLeagueId !== targetDivisionId) {
				return;
			}

			const sortedGames = [...scheduleData].sort((a, b) => {
				const keyA = normalizeDateString(getGameDateKey(a));
				const keyB = normalizeDateString(getGameDateKey(b));
				if (!keyA) return 1;
				if (!keyB) return -1;
				return keyA.localeCompare(keyB);
			});
			
			games = sortedGames;
			void loadBettings(sortedGames);
			void scrollToFirstScheduledGame();
			
		} catch (error) {
			console.error("Load Schedule Error:", error);
			games = [];
			errorMessage = `Ошибка загрузки расписания`;
		} finally { 
			if (selectedLeagueId === targetDivisionId) {
				loadingSchedule = false; 
			}
		}
	}

	// --- ЛОГИКА КОНКУРСА ПРОГНОЗОВ ---

	async function loadBettings(gameList) {
		const ids = (gameList || []).map((game) => String(game?.providerId ?? '')).filter(Boolean);
		if (!ids.length) {
			bettingsByProviderId = {};
			return;
		}

		bettingLoading = true;
		try {
			bettingsByProviderId = await fetchBettings(ids);
		} catch (error) {
			console.error('Load bettings error:', error);
		} finally {
			bettingLoading = false;
		}
	}

	function getTeamName(team) {
		return team?.shortName || team?.name || team?.abcName || '';
	}

	// Описывает состояние одной стороны карточки матча (1 или 2).
	function betSideState(game, side, bettings) {
		const providerId = String(game?.providerId ?? '');
		const info = providerId ? bettings[providerId] : null;
		const pct = getBetPercentages(info);
		const finished = Boolean(info?.isResolved) || isCompletedGame(game);
		const online = isOnlineGame(game);
		const sidePct = side === 1 ? pct.p1 : pct.p2;
		const isYourBet = info?.yourBet === side;
		const result = info?.result;
		const youWon = finished && isYourBet && result !== null && result !== undefined && result === info?.yourBet;
		const youLost = finished && isYourBet && result !== null && result !== undefined && result !== info?.yourBet;
		return { sidePct, finished, online, isYourBet, youWon, youLost, total: pct.total };
	}

	async function handleBet(game, team) {
		if (!game || !projectId) return;
		betError = '';
		pendingBet = { game, team };
		if (!isAuthorized()) {
			authOpen = true;
			return;
		}
		await placePendingBet();
	}

	async function handleAuthorized() {
		if (pendingBet) {
			await placePendingBet();
		}
	}

	async function placePendingBet() {
		const next = pendingBet;
		pendingBet = null;
		if (!next || !isAuthorized()) return;

		const { game, team } = next;
		const providerId = String(game?.providerId ?? '');
		if (!providerId) return;

		placingBetKey = providerId;
		try {
			await makeBet({
				projectId,
				providerId,
				bettingDate: resolveBettingDate(game?.dateTime),
				result1Name: getTeamName(game?.team1),
				result2Name: getTeamName(game?.team2),
				bet: team
			});
			await loadBettings(games);
		} catch (error) {
			console.error('Make bet error:', error);
			betError = error.message || 'Не удалось сделать ставку';
		} finally {
			placingBetKey = '';
		}
	}

	// --- ЛОГИКА СВАЙПА ---
	function handleTouchStart(e) {
		isDragging = true;
		startX = e.touches ? e.touches[0].pageX : e.pageX;
		scrollLeftStart = railElement.scrollLeft;
		railElement.style.scrollBehavior = 'auto';
	}

	function handleTouchMove(e) {
		if (!isDragging) return;
		const x = e.touches ? e.touches[0].pageX : e.pageX;
		const walk = (x - startX) * 1.5;
		railElement.scrollLeft = scrollLeftStart - walk;
	}

	function handleTouchEnd() {
		isDragging = false;
		railElement.style.scrollBehavior = 'smooth';
	}

	function scrollRail(direction) {
		if (!railElement) return;
		tick().then(() => {
			const cards = railElement.querySelectorAll('.card');
			if (!cards.length) return;
			const cardWidth = cards[0].getBoundingClientRect().width;
			const gap = parseFloat(getComputedStyle(railElement).gap) || 0;
			railElement.scrollBy({ left: direction * (cardWidth + gap), behavior: 'smooth' });
		});
	}

	function scrollToFirstScheduledGame() {
		if (!railElement) return;
		tick().then(() => {
			const cards = railElement.querySelectorAll('.card');
			if (!cards.length) return;

			let targetCard = null;
			for (let i = 0; i < filteredGames.length && i < cards.length; i++) {
				if (!isCompletedGame(filteredGames[i])) {
					targetCard = cards[i];
					break;
				}
			}

			const maxScroll = Math.max(0, railElement.scrollWidth - railElement.clientWidth);
			let scrollLeft;
			if (targetCard) {
				const railRect = railElement.getBoundingClientRect();
				const cardRect = targetCard.getBoundingClientRect();
				const cardCenter = cardRect.left + cardRect.width / 2 - railRect.left + railElement.scrollLeft;
				scrollLeft = cardCenter - railElement.clientWidth / 2;
			} else {
				scrollLeft = maxScroll;
			}

			railElement.style.scrollBehavior = 'auto';
			railElement.scrollLeft = Math.max(0, Math.min(scrollLeft, maxScroll));
			railElement.style.scrollBehavior = 'smooth';
		});
	}

	function handleDateChange(event) {		
		selectedDateKey = event?.detail ?? selectedDateKey;
	}

	function openGame(game, event) {
		// Клик по кнопке ставки/прогноза не должен уводить на страницу игры.
		const target = event?.target;
		if (target && typeof target.closest === 'function' && target.closest('.card__btn, .card__score--bet')) {
			return;
		}
		const id = game?.providerId || game?.id;
		if (!id) return;
		window.location.href = gameUrl(id, { tag: selectedLeagueTag || tag, compId: selectedLeagueId });
	}

	onMount(() => {
		const root = host?.getRootNode();
		if (root instanceof ShadowRoot) injectGlobalStyles(root);
		window.addEventListener(LEAGUE_CHANGED_EVENT, handleGlobalLeagueChange);
		loadDivisions();
	});

	onDestroy(() => {
		window.removeEventListener(LEAGUE_CHANGED_EVENT, handleGlobalLeagueChange);
	});
</script>

<div class="statusbar" data-project-id={projectId} bind:this={host}>
	<!-- ИЗМЕНЕНО: Показываем статусы загрузки и ошибок всегда -->
	{#if errorMessage}
		<div class="widget-status" style="color: red;">{errorMessage}</div>
	{:else if loadingDivisions}
		<div class="widget-status"><ilb-loader size="64px"></ilb-loader></div>
	{:else if !divisions.length}
		<div class="widget-status">Дивизионы не найдены</div>
	{:else}
		<!-- ИЗМЕНЕНО: Весь интерфейс рендерится ТОЛЬКО когда дивизионы загружены -->
		<div class="statusbar__filter">
			<WidgetSelect 
				className="--light" 
				name="league" 
				options={leagueOptions} 
				bind:value={selectedLeagueId} 
				disabled={loadingSchedule} 
			/>
			<WidgetSelect 
				className="--light" 
				name="date" 
				options={dateOptions} 
				bind:value={selectedDateKey} 
				disabled={loadingSchedule || !dateOptions.length} 
				emptyLabel="Нет дат" 
				on:change={handleDateChange} 
			/>
		</div>

		{#if loadingSchedule}
			<div class="widget-status"><ilb-loader size="64px"></ilb-loader></div>
		{/if}

		{#if betError}
			<div class="widget-status bet-status" role="alert">{betError}</div>
		{/if}

		<div class="widget-rail-wrap">
			{#if !loadingSchedule && !filteredGames.length}
				<div class="widget-status">Матчи не найдены</div>
			{:else}
				<button class="widget-rail-nav --prev" type="button" aria-label="Предыдущие" on:click={() => scrollRail(-1)}>‹</button>
				<button class="widget-rail-nav --next" type="button" aria-label="Следующие" on:click={() => scrollRail(1)}>›</button>

				<div 
					class="widget-rail" 
					bind:this={railElement}
					on:mousedown={handleTouchStart}
					on:mousemove={handleTouchMove}
					on:mouseup={handleTouchEnd}
					on:mouseleave={handleTouchEnd}
					on:touchstart={handleTouchStart}
					on:touchmove={handleTouchMove}
					on:touchend={handleTouchEnd}
				>
					{#each filteredGames as item, index (gameKey(item, index))}
						{@const left = betSideState(item, 1, bettingsByProviderId)}
						{@const right = betSideState(item, 2, bettingsByProviderId)}
						<div class="card card--clickable" role="button" tabindex="0" on:click={(event) => openGame(item, event)}>
							<div class="card__left">
								<div class="card__team">
									{#if item.team1?.logo}<img src={item.team1.logo} class="card__img" alt={item.team1?.name || 'team'}>{/if}
									<h3 class="card__title">{item.team1?.shortName || item.team1?.name || '-'}</h3>
								</div>
								{#if left.finished}
									{#if left.isYourBet}
										<span class="card__descr">{left.youWon ? 'Вы выиграли' : (left.youLost ? 'Вы проиграли' : 'Ваша ставка')}</span>
									{/if}
									<span class="card__score" class:--green={left.youWon} class:--red={left.youLost}>{left.sidePct}%</span>
								{:else if left.online}
									<span class="card__score" class:--green={hasScore(item) && item.score1 > item.score2}>
										{item.score1 ?? '-'}
									</span>
								{:else if left.total > 0}
									{#if left.isYourBet}<span class="card__descr">Ваша ставка</span>{/if}
									<button
										type="button"
										class="card__score card__score--bet"
										class:--green={left.isYourBet}
										disabled={placingBetKey === String(item.providerId ?? '')}
										on:click|stopPropagation={() => handleBet(item, 1)}
									>
										{left.sidePct}%
									</button>
								{:else}
									<button
										type="button"
										class="card__btn"
										disabled={placingBetKey === String(item.providerId ?? '')}
										on:click|stopPropagation={() => handleBet(item, 1)}
									>
										Прогноз
									</button>
								{/if}
							</div>
							<div class="card__middle">
								<div class="card__datetime">
									<span class="card__date">{item.dateTime?.localDate || 'Без даты'}</span>
									{#if isOnlineGame(item)}
										<span class="card__time --live">{getOnlineInfo(item)}</span>
									{:else}
										<span class="card__time">{getGameTimeLabel(item)}</span>
									{/if}
								</div>
								{#if left.finished && hasScore(item)}
									<div class="card__results">
										<span class="card__result">{item.score1 ?? '-'}</span>
										<span class="card__result">{item.score2 ?? '-'}</span>
									</div>
								{/if}
							</div>
							<div class="card__right">
								<div class="card__team">
									{#if item.team2?.logo}<img src={item.team2.logo} class="card__img" alt={item.team2?.name || 'team'}>{/if}
									<h3 class="card__title">{item.team2?.shortName || item.team2?.name || '-'}</h3>
								</div>
								{#if right.finished}
									{#if right.isYourBet}
										<span class="card__descr">{right.youWon ? 'Вы выиграли' : (right.youLost ? 'Вы проиграли' : 'Ваша ставка')}</span>
									{/if}
									<span class="card__score" class:--green={right.youWon} class:--red={right.youLost}>{right.sidePct}%</span>
								{:else if right.online}
									<span class="card__score" class:--green={hasScore(item) && item.score2 > item.score1}>
										{item.score2 ?? '-'}
									</span>
								{:else if right.total > 0}
									{#if right.isYourBet}<span class="card__descr">Ваша ставка</span>{/if}
									<button
										type="button"
										class="card__score card__score--bet"
										class:--green={right.isYourBet}
										disabled={placingBetKey === String(item.providerId ?? '')}
										on:click|stopPropagation={() => handleBet(item, 2)}
									>
										{right.sidePct}%
									</button>
								{:else}
									<button
										type="button"
										class="card__btn"
										disabled={placingBetKey === String(item.providerId ?? '')}
										on:click|stopPropagation={() => handleBet(item, 2)}
									>
										Прогноз
									</button>
								{/if}
							</div>
						</div>
					{/each}
				</div>
			{/if}
		</div>
	{/if}
</div>

<AuthDialog bind:open={authOpen} intro="Войдите по номеру телефона, чтобы сделать прогноз на матч." on:authorized={handleAuthorized} on:close={() => { pendingBet = null; }} />

<style>
	:host { display: block; width: 100%; box-sizing: border-box; }
	.widget-rail-wrap { position: relative; width: 100%; overflow: hidden; }
	.widget-status { margin: 8px 0; color: #53657b; }

	.bet-status {
		margin: 0 16px 8px;
		color: #a52f2f;
		font-size: 13px;
	}

	.widget-rail {
		display: flex;
		align-items: stretch;
		gap: 12px;
		padding: 12px 40px;
		overflow-x: auto;
		overflow-y: hidden;
		scroll-snap-type: x mandatory;
		scroll-behavior: smooth;
		scrollbar-width: none;
		-ms-overflow-style: none;
		width: 100%;
		box-sizing: border-box;
		cursor: grab;
		user-select: none;
		-webkit-user-select: none;
	}
	.widget-rail:active { cursor: grabbing; }
	.widget-rail::-webkit-scrollbar { display: none; }

	.widget-rail .card {
		flex: 0 0 auto;
		min-width: 280px;
		scroll-snap-align: center;
		box-shadow: 0 2px 10px rgba(0, 36, 77, 0.08);
		transition: box-shadow 0.2s ease, transform 0.2s ease;
	}
	.widget-rail .card--clickable:hover { box-shadow: 0 6px 18px rgba(0, 36, 77, 0.16); transform: translateY(-2px); }

	.widget-rail-nav {
		position: absolute; top: 50%; z-index: 2; transform: translateY(-50%);
		width: 32px; height: 32px; border: 0; border-radius: 50%;
		background: #00244d; color: #fff; font-size: 18px; line-height: 1;
		cursor: pointer; display: flex; align-items: center; justify-content: center;
		transition: background-color 0.2s ease;
	}
	.widget-rail-nav:hover { background: #003a75; }
	.widget-rail-nav.--prev { left: 0; }
	.widget-rail-nav.--next { right: 0; }

	.card--clickable { cursor: pointer; }
	.card__time.--live { color: #d93025; font-weight: 700; }

	/* Кликабельная кнопка ставки (процент) */
	.card__score--bet {
		cursor: pointer;
		transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
	}
	.card__score--bet:hover:not(:disabled) {
		border-color: #b3c2d6;
		background-color: #eef2f8;
	}
	.card__score--bet:disabled {
		cursor: default;
		opacity: 0.7;
	}

	/* Стиль для лидера */
	.card__score.--green {
		color: white;
		font-weight: 700;
	}
	.card__score.--red {
		color: white;
		font-weight: 700;
	}

	@media (max-width: 760px) {
		.widget-rail { padding: 12px 28px; }
		.widget-rail .card { width: 100%; min-width: 100%; }
	}
</style>
